// Embedded-SSO landing (/login when REACT_APP_AUTH_MODE=embedded). No participant
// picker and no IdP redirect: we run the parent handshake, exchange the handoff token
// for a dashboard session token at /sso/accept, store it, then reuse the EXISTING auth
// flow — useAuth().login(principal) persists the session id and calls /me (the API
// client attaches the session token as Bearer). So auth.slice, RequireAuth and the "/"
// gate are reused unchanged, exactly like the OIDC AuthCallback. See EMBEDDED_SSO.md.
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Surface, Spinner, ErrorState } from '../../components/reusable';
import { UserIcon, ArrowRightIcon } from '../../components/icons/Icons';
import { apiPost } from '../../services/api/client';
import { useAuth } from '../AuthProvider';
import { PARENT_ORIGINS, HANDSHAKE_TIMEOUT_MS } from './embeddedConfig';
import { requestParentToken } from './postMessageHandshake';
import { setEmbeddedToken } from './embeddedToken';
import { setLegacySessionParameter } from './legacySession';
import { setParentCapabilities, isFramed, navigateParent } from './parentNavigation';
import { buildLegacyUrl } from '../../utils/legacyPortal';
import { STORAGE_KEYS } from '../../config/constants';
import '../../pages/Login/login.css';

// Module-level guard (not a ref): survives the StrictMode effect re-invoke so the
// handshake + /sso/accept run once. A fresh full-page load resets it.
let handshakeStarted = false;

// Backend `code`s that describe the LOGIN rather than a transient failure, each with its
// own page (a retry cannot change either):
//   no_participant_identity — the handoff carried a login name but no Relius GUID.
//   no_participant_data     — the GUID is known but the warehouse has no plan row for it.
//   user_not_found          — the fixture/data source has no account for this login.
// Only these codes get the notice page. A 404 WITHOUT one of them is not a verdict about
// the login (a misrouted or undeployed API answers 404 too), so it keeps the generic,
// retryable error — FM-2771 review.
const NOTICE_BY_CODE = {
  no_participant_identity: 'notLinked',
  no_participant_data: 'noData',
  user_not_found: 'noData',
};
// Remembered per tab: the Relius handoff token is single-use, so a refresh after such a
// verdict yields a 401 that says nothing about the login. Keep showing the verdict rather
// than a misleading "session expired".
const NOTICE_FLAG = 'pcs_embed_not_linked';
const noticeFlag = {
  get: () => { try { return sessionStorage.getItem(NOTICE_FLAG) || null; } catch { return null; } },
  set: (variant) => { try { sessionStorage.setItem(NOTICE_FLAG, variant); } catch { /* storage blocked */ } },
  clear: () => { try { sessionStorage.removeItem(NOTICE_FLAG); } catch { /* storage blocked */ } },
};

// Reload the Relius page that frames us, so it issues a fresh handoff token (FM-3035).
// Same page on the host that actually framed us (dev / uat / prod share one build); the
// configured legacy host only when the parent's origin is unknown or not allowed.
function reopenParentPage(persona) {
  let url = buildLegacyUrl(persona === 'sponsor' ? 'sponsorDashboard' : 'participantDashboard');
  let parent = window.location.ancestorOrigins?.[0] || null;
  if (!parent) { try { parent = new URL(document.referrer).origin; } catch { parent = null; } }
  if (parent && PARENT_ORIGINS.includes(parent)) {
    const target = new URL(url);
    url = `${parent}${target.pathname}${target.search}`;
  }
  if (!navigateParent(url)) window.open(url, '_top');
}

// Best-effort decode of a JWT's `sub` for the local session id. The backend derives
// the real principal from the token independently; this is only the localStorage id.
function decodeSub(jwt) {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.sub || null;
  } catch {
    return null;
  }
}

// Diagnostic: which claims the parent's handoff carries and whether any is a Relius GUID
// (32 hex, optionally hyphenated). Names and shapes only — values never reach the console.
function describeHandoff(jwt) {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const shapes = {};
    let hasGuid = false;
    Object.entries(payload).forEach(([name, value]) => {
      const s = String(value ?? '');
      const isGuid = /^[0-9a-f]{32}$/i.test(s) || /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(s);
      if (isGuid) hasGuid = true;
      shapes[name] = isGuid ? 'GUID' : (/^\d+$/.test(s) ? `digits${s.length}` : `str${s.length}`);
    });
    return { hasReliusGuid: hasGuid, claims: shapes };
  } catch {
    return { hasReliusGuid: false, claims: 'undecodable' };
  }
}

// "Click Classic View, top right" — the one thing the user can do when this dashboard
// cannot sign them in or has nothing to show. The button is on the Relius page that
// frames us, so it is only offered when we KNOW we are framed (FM-2771).
function ClassicViewHint() {
  return (
    <p className="login-hint">
      <ArrowRightIcon className="login-hint__arrow" width={18} height={18} aria-hidden="true" />
      <span>
        <Trans
          i18nKey="login.embedded.classicViewHint"
          components={{ button: <strong className="login-hint__button" /> }}
        />
      </span>
    </p>
  );
}

// `variant` picks the copy: 'notLinked' | 'noData' (keys login.embedded.<variant>*).
// The `<variant>Help` line is optional: 'noData' has none (FM-2771 — the Classic View
// hint is the whole call to action there), so a missing key renders nothing, not the key.
function LoginNotice({ variant }) {
  const { t, i18n } = useTranslation();
  const helpKey = `login.embedded.${variant}Help`;
  const help = i18n.exists(helpKey) ? t(helpKey) : '';
  return (
    <div className="login-page">
      <Surface className="login-card" padding="none">
        {/* No brand row: this page is served inside white-label plan websites, where PCS
            is not a name the user knows (FM-2771). The notice carries its own icon. */}
        <div className="login-notice" role="status">
          <span className="login-notice__icon"><UserIcon width={22} height={22} /></span>
          <h1 className="login-notice__title">{t(`login.embedded.${variant}Title`)}</h1>
          <p className="login-notice__text">{t(`login.embedded.${variant}Message`)}</p>
          <ClassicViewHint />
          {help && <p className="login-notice__help">{help}</p>}
        </div>
      </Surface>
    </div>
  );
}

LoginNotice.propTypes = {
  variant: PropTypes.oneOf(['notLinked', 'noData']).isRequired,
};

export function EmbeddedLogin() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  // Where the parent framed us. RequireAuth parks it here on its way to /login, exactly
  // as the dummy and OIDC logins already consume it. The Relius portal lands a sponsor on
  // /sponsor/* and a participant elsewhere, so this path is the session's persona — the
  // handoff token itself carries none.
  const from = location.state?.from?.pathname;
  // Sent in BOTH directions: a participant page must stay a participant session even
  // when the handoff carries a GUID that happens to administer plans.
  const persona = from?.startsWith('/sponsor') ? 'sponsor' : 'participant';

  useEffect(() => {
    if (handshakeStarted) return;
    handshakeStarted = true;
    (async () => {
      try {
        const { handoffToken, sessionParameter, capabilities } = await requestParentToken({
          parentOrigins: PARENT_ORIGINS,
          timeoutMs: HANDSHAKE_TIMEOUT_MS,
        });
        // Extras from the same reply, both optional. They gate how legacy-portal
        // hand-offs behave (deep-link RANDOMNUM + one-tab navigation), not sign-in, so
        // they are recorded before the exchange and never block it.
        setLegacySessionParameter(sessionParameter);
        setParentCapabilities(capabilities);
        // eslint-disable-next-line no-console
        console.info('[embedded-login] handoff claims', describeHandoff(handoffToken));
        const { accessToken } = await apiPost('/sso/accept', { handoffToken, persona });
        if (!accessToken) throw new Error(t('login.embedded.noSession'));
        setEmbeddedToken(accessToken); // client.js now sends it as Bearer
        // Reuse the existing flow: persists the id + bootstraps /me with the Bearer token.
        await login(String(decodeSub(accessToken) || 'participant'));
        noticeFlag.clear();
        // A fresh Relius entry starts from the plan the handoff names (else the first),
        // never from whatever plan this browser last had open — that memory is for the
        // "Home" crumb within a session, not for the way in.
        try { localStorage.removeItem(STORAGE_KEYS.SPONSOR_PLAN_ID); } catch { /* storage blocked */ }
        // Back to where the parent aimed us, so a sponsor's /sponsor/<planId> deep link
        // survives the login detour. "/" still routes each persona to its own home.
        navigate(from || '/', { replace: true });
      } catch (err) {
        handshakeStarted = false; // allow a retry
        // eslint-disable-next-line no-console
        console.error('[embedded-login] sign-in failed', { status: err?.status, code: err?.code, message: err?.message });
        // A backend verdict about the login gets the friendly notice page; whatever its
        // detail says stays off screen (FM-2771: it used to carry the user's login name).
        const variant = NOTICE_BY_CODE[err?.code]
          || (err?.status === 401 ? noticeFlag.get() : null);
        if (variant) {
          noticeFlag.set(variant);
          setError({ notice: variant });
          return;
        }
        // A handshake timeout almost always means someone opened the dashboard directly
        // instead of from the portal. `handoff_replayed` is a page reload: the portal
        // mints its single-use handoff token once per login and re-echoes it, which is
        // NOT the session ending. Any other 401 is a stale/invalid token. Whatever the
        // cause, the user sees copy only — the technical detail goes to the console.
        let message = t('login.embedded.genericError');
        let reopenParent = false;
        if (err?.code === 'HANDSHAKE_TIMEOUT') message = t('login.embedded.openFromPortal');
        else if (err?.status === 401) {
          message = err?.code === 'handoff_replayed'
            ? t('login.embedded.refreshNotSupported')
            : t('login.embedded.sessionExpired');
          // A frame reload only re-echoes the same spent token, so "Try again" cannot
          // retry here: when framed it reloads the Relius page around us instead, which
          // issues a fresh token (FM-3035). The Classic View hint is the other way
          // forward (FM-2771); a 401 means the parent answered, so that button exists.
          reopenParent = isFramed();
        }
        setError({ message, classicView: err?.status === 401, reopenParent });
      }
    })();
  }, [login, navigate, t, from, persona]);

  if (error?.notice) return <LoginNotice variant={error.notice} />;

  // 401 while framed: reload the parent page so it issues a fresh handoff token. 401
  // unframed: nothing a retry could do, the hint is the whole call to action. Anything
  // else (timeout, generic fault): reload this frame.
  const retryFor = (e) => {
    if (e.reopenParent) return () => reopenParentPage(persona);
    if (e.classicView) return undefined;
    return () => { setError(null); window.location.reload(); };
  };

  return (
    <div className="login-page">
      {error
        ? (
          <div className="login-failure">
            <ErrorState message={error.message} onRetry={retryFor(error)} />
            {error.classicView && <ClassicViewHint />}
          </div>
        )
        : <Spinner size="lg" label={t('login.embedded.signingIn')} />}
    </div>
  );
}

export default EmbeddedLogin;
